(() => {
  'use strict';

  if (customElements.get('frequently-bought-together')) return;

  class FrequentlyBoughtTogether extends HTMLElement {
    connectedCallback() {
      this.source = this.dataset.source;
      this.status = this.querySelector('[data-fbt-status]');
      this.items = [...this.querySelectorAll('[data-fbt-item]')];
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onVariantUpdate = this.onVariantUpdate.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('fbt:variant:update', this.onVariantUpdate);
      this.syncFromCart();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('fbt:variant:update', this.onVariantUpdate);
    }

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: {
          Accept: 'application/json',
          ...(options.body ? {'Content-Type': 'application/json'} : {}),
          ...options.headers,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || this.dataset.errorMessage);
      return data;
    }

    findLine(cart, item) {
      const variantId = Number(item.dataset.variantId);
      return this.findLineByVariant(cart, variantId);
    }

    findLineByVariant(cart, variantId) {
      return cart.items.find((line) =>
        line.variant_id === variantId && line.properties && line.properties._fbt_source === this.source
      );
    }

    findProductLine(cart, item) {
      const productId = Number(item.dataset.productId);
      return cart.items.find((line) =>
        line.product_id === productId && line.properties && line.properties._fbt_source === this.source
      );
    }

    async syncFromCart() {
      try {
        const cart = await this.fetchJSON(this.dataset.cartUrl);
        this.items.forEach((item) => {
          const line = this.findProductLine(cart, item);
          const checkbox = item.querySelector('[data-fbt-checkbox]');
          const quantity = item.querySelector('[data-fbt-quantity]');
          if (line && line.variant_id !== Number(item.dataset.variantId)) {
            const variant = this.findVariantData(item, line.variant_id);
            if (variant) this.applyVariantDisplay(item, variant);
          }
          checkbox.checked = Boolean(line);
          item.classList.toggle('is-selected', Boolean(line));
          if (line) {
            item.dataset.lineKey = line.key;
            quantity.value = line.quantity;
          } else {
            delete item.dataset.lineKey;
          }
        });
      } catch (error) {
        this.announce(error.message || this.dataset.errorMessage, true);
      }
    }

    async onChange(event) {
      const item = event.target.closest('[data-fbt-item]');
      if (!item || item.classList.contains('is-loading')) return;

      if (event.target.matches('[data-fbt-checkbox]')) {
        await this.toggleItem(item, event.target.checked);
      } else if (event.target.matches('[data-fbt-quantity]')) {
        this.normalizeQuantity(event.target);
        if (item.classList.contains('is-selected')) await this.updateQuantity(item);
      }
    }

    async onClick(event) {
      const button = event.target.closest('[data-fbt-decrease], [data-fbt-increase]');
      if (!button) return;
      event.preventDefault();

      const item = button.closest('[data-fbt-item]');
      if (!item || item.classList.contains('is-loading')) return;
      const input = item.querySelector('[data-fbt-quantity]');
      const current = this.normalizeQuantity(input);
      input.value = button.hasAttribute('data-fbt-increase') ? current + 1 : Math.max(1, current - 1);
      if (item.classList.contains('is-selected')) await this.updateQuantity(item);
    }

    async onVariantUpdate(event) {
      const updater = event.target.closest('[data-fbt-product-update]');
      const item = updater?.closest('[data-fbt-item]');
      if (!item || !this.contains(item)) return;

      event.preventDefault();
      const variant = event.detail?.variant;
      if (!variant) {
        event.detail?.fail?.(new Error('The selected variant could not be loaded.'));
        return;
      }

      try {
        if (item.classList.contains('is-selected')) {
          await this.replaceSelectedVariant(item, variant);
        } else {
          this.applyVariantDisplay(item, variant);
        }
        event.detail?.complete?.();
      } catch (error) {
        event.detail?.fail?.(error);
      }
    }

    async replaceSelectedVariant(item, variant) {
      const oldVariantId = Number(item.dataset.variantId);
      const newVariantId = Number(variant.id);
      const quantity = this.normalizeQuantity(item.querySelector('[data-fbt-quantity]'));
      let addedLine = null;
      this.setLoading(item, true);

      try {
        const cartBefore = await this.fetchJSON(this.dataset.cartUrl);
        const oldLine = this.findLineByVariant(cartBefore, oldVariantId);

        await this.addVariant(newVariantId, quantity);
        const cartWithNewLine = await this.fetchJSON(this.dataset.cartUrl);
        addedLine = this.findLineByVariant(cartWithNewLine, newVariantId);

        const finalCart = oldLine ? await this.changeLine(oldLine.key, 0) : cartWithNewLine;
        this.applyVariantDisplay(item, variant);
        const finalLine = this.findLineByVariant(finalCart, newVariantId);
        if (finalLine) item.dataset.lineKey = finalLine.key;
        item.querySelector('[data-fbt-checkbox]').checked = true;
        item.classList.add('is-selected');
        this.refreshThemeCart(finalCart);
      } catch (error) {
        if (addedLine) {
          try {
            await this.changeLine(addedLine.key, 0);
          } catch (rollbackError) {
            console.error(rollbackError);
          }
        }
        this.announce(error.message || this.dataset.errorMessage, true);
        await this.syncFromCart();
        throw error;
      } finally {
        this.setLoading(item, false);
      }
    }

    addVariant(variantId, quantity) {
      return this.fetchJSON(this.dataset.cartAddUrl, {
        method: 'POST',
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity,
            properties: {_fbt_source: this.source},
          }],
        }),
      });
    }

    findVariantData(item, variantId) {
      const variantsElement = item.querySelector('[data-fbt-variants]');
      if (!variantsElement) return null;

      try {
        const variants = JSON.parse(variantsElement.textContent);
        return variants.find((variant) => Number(variant.id) === Number(variantId)) || null;
      } catch (error) {
        return null;
      }
    }

    applyVariantDisplay(item, variant) {
      item.dataset.variantId = variant.id;

      const variantTitle = item.querySelector('[data-fbt-variant-title]');
      if (variantTitle) variantTitle.textContent = variant.title;

      const price = item.querySelector('[data-fbt-price]');
      if (price) price.textContent = window.theme.formatMoney(variant.price, window.theme.moneyFormat);

      const compare = item.querySelector('[data-fbt-compare]');
      if (compare) {
        const showCompare = Number(variant.compare_at_price) > Number(variant.price);
        compare.textContent = showCompare
          ? window.theme.formatMoney(variant.compare_at_price, window.theme.moneyFormat)
          : '';
        compare.hidden = !showCompare;
      }

      const image = item.querySelector('.fbt__variant-image');
      const imageUrl = variant.featured_image?.src || variant.featured_image || '';
      if (image && imageUrl) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }

      const updater = item.querySelector('[data-fbt-product-update]');
      if (updater) updater.dataset.variantId = variant.id;
    }

    normalizeQuantity(input) {
      const value = Math.max(1, parseInt(input.value, 10) || 1);
      input.value = value;
      return value;
    }

    async toggleItem(item, shouldAdd) {
      const checkbox = item.querySelector('[data-fbt-checkbox]');
      this.setLoading(item, true);

      try {
        if (shouldAdd) {
          await this.addVariant(
            Number(item.dataset.variantId),
            this.normalizeQuantity(item.querySelector('[data-fbt-quantity]'))
          );
          item.classList.add('is-selected');
          this.announce(this.dataset.addedMessage);
        } else {
          const cart = await this.fetchJSON(this.dataset.cartUrl);
          const line = this.findLine(cart, item);
          if (line) {
            await this.changeLine(line.key, 0);
          }
          item.classList.remove('is-selected');
          delete item.dataset.lineKey;
          this.announce(this.dataset.removedMessage);
        }

        const cart = await this.fetchJSON(this.dataset.cartUrl);
        const line = this.findLine(cart, item);
        if (line) item.dataset.lineKey = line.key;
        checkbox.checked = Boolean(line);
        item.classList.toggle('is-selected', Boolean(line));
        this.refreshThemeCart(cart);
      } catch (error) {
        checkbox.checked = !shouldAdd;
        item.classList.toggle('is-selected', !shouldAdd);
        this.announce(error.message || this.dataset.errorMessage, true);
      } finally {
        this.setLoading(item, false);
      }
    }

    async updateQuantity(item) {
      this.setLoading(item, true);
      try {
        const cartBefore = await this.fetchJSON(this.dataset.cartUrl);
        const line = this.findLine(cartBefore, item);
        if (!line) {
          item.querySelector('[data-fbt-checkbox]').checked = false;
          item.classList.remove('is-selected');
          return;
        }
        const cart = await this.changeLine(line.key, this.normalizeQuantity(item.querySelector('[data-fbt-quantity]')));
        const updatedLine = this.findLine(cart, item);
        if (updatedLine) item.dataset.lineKey = updatedLine.key;
        this.refreshThemeCart(cart);
      } catch (error) {
        this.announce(error.message || this.dataset.errorMessage, true);
        await this.syncFromCart();
      } finally {
        this.setLoading(item, false);
      }
    }

    changeLine(key, quantity) {
      return this.fetchJSON(this.dataset.cartChangeUrl, {
        method: 'POST',
        body: JSON.stringify({id: key, quantity}),
      });
    }

    refreshThemeCart(cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: {cartCount: cart.item_count},
      }));
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
        bubbles: true,
        detail: {suppressProductAdded: true},
      }));
    }

    setLoading(item, loading) {
      item.classList.toggle('is-loading', loading);
      item.setAttribute('aria-busy', String(loading));
      item.querySelectorAll('button, input').forEach((control) => {
        if (!control.closest('.is-unavailable')) control.disabled = loading;
      });
    }

    announce(message, isError = false) {
      if (!message) return;
      this.status.textContent = '';
      requestAnimationFrame(() => { this.status.textContent = message; });
      if (typeof window.showToast === 'function') {
        window.showToast(message);
        return;
      }

      const toast = document.createElement('div');
      toast.className = `fbt-toast${isError ? ' fbt-toast--error' : ''}`;
      toast.textContent = message;
      toast.setAttribute('role', isError ? 'alert' : 'status');
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 200);
      }, 2600);
    }
  }

  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
})();
